import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import portalClient, { setPortalTokens, clearPortalTokens, getPortalTokens } from './portalClient';

// Session state for the CLIENT portal. Entirely separate from auth-lib's
// AuthContext, which holds the firm's staff session — a browser can hold both
// at once without either noticing.

const PortalAuthContext = createContext(null);

export function PortalAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore an existing session on load. A stored token that no longer works is
  // cleared rather than left to fail every subsequent request.
  useEffect(() => {
    const { accessToken } = getPortalTokens();
    if (!accessToken) {
      setLoading(false);
      return;
    }
    portalClient
      .get('/api/portal/auth/me')
      .then((res) => {
        setUser(res.data.user);
        setClient(res.data.client);
      })
      .catch(() => clearPortalTokens())
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await portalClient.post('/api/portal/auth/login', { email, password });
    setPortalTokens(res.data.accessToken, res.data.refreshToken);
    setUser(res.data.user);
    setClient(res.data.client);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    const { refreshToken } = getPortalTokens();
    try {
      // Best-effort server-side revocation; the local session is cleared either
      // way, so a network failure can't strand someone signed in.
      await portalClient.post('/api/portal/auth/logout', { refreshToken });
    } catch {
      /* ignore */
    }
    clearPortalTokens();
    setUser(null);
    setClient(null);
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const res = await portalClient.post('/api/portal/auth/change-password', {
      currentPassword,
      newPassword,
    });
    // The server rotates the session on a password change, so adopt the new
    // tokens rather than continuing with the now-revoked ones.
    setPortalTokens(res.data.accessToken, res.data.refreshToken);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const value = useMemo(
    () => ({ user, client, loading, login, logout, changePassword, setUser }),
    [user, client, loading, login, logout, changePassword]
  );

  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
}

export const usePortalAuth = () => {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error('usePortalAuth must be used inside a PortalAuthProvider');
  return ctx;
};

// Gate for every portal page. Also enforces the forced password change: while
// `mustChangePassword` is set the server refuses everything else, so sending
// them anywhere but that screen would just render an error page.
export function PortalRoute({ children }) {
  const { user, loading } = usePortalAuth();
  const location = useLocation();

  if (loading) {
    return <div className="portal-boot">Loading…</div>;
  }
  if (!user) {
    return <Navigate to="/portal/login" replace state={{ from: location.pathname }} />;
  }
  if (user.mustChangePassword && location.pathname !== '/portal/set-password') {
    return <Navigate to="/portal/set-password" replace />;
  }
  return children;
}

export default PortalAuthContext;
